import type {
  ActiveShiftStatus,
  AttendanceSummary,
  AttendanceException,
  AttendanceExceptionState,
  AttendanceExceptionType,
  BulkResolveBody,
  BulkResolveResult,
  CreateAdjustmentBody,
  CreateAdjustmentResult,
  PayrollImpact,
  ProcessAttendanceBody,
  ProcessAttendanceResult,
  ProcessedAttendanceRecord,
  QuickResolveBody,
  QuickResolveResult,
  RawAttendanceEvent,
  ResolveExceptionBody,
} from '@mervonn/shared-types';
import { apiList, apiRequest } from '../../lib/api/client';

export interface RecordsFilter {
  employee_id?: string;
  branch_id?: string;
  from?: string;
  to?: string;
  // Stage 4 Phase 2 — Employee Detail's "Recent Attendance" card wants the
  // last 5, not the usual 200-row page every other caller of this function
  // wants; optional so every existing call site is unaffected.
  per_page?: number;
}

export interface ExceptionsFilter {
  exception_type?: AttendanceExceptionType;
  state?: AttendanceExceptionState;
  // Stage 7 Phase 2A — server-side filters (they were stubs before).
  employee_id?: string;
  branch_id?: string;
  shift_id?: string;
  search?: string;
  page?: number;
}

export interface RawEventsFilter {
  device_user_id?: string;
  from?: string;
  to?: string;
  unresolved?: boolean;
}

export const EXCEPTIONS_PER_PAGE = 25;
const BULK_RESOLVE_MAX = 100;

export const attendanceApi = {
  records: (f: RecordsFilter) =>
    apiRequest<ProcessedAttendanceRecord[]>('/attendance/records', {
      query: { ...f, per_page: f.per_page ?? 200 },
    }),

  /** One page of records with the total. `GET /attendance/records` answers `{ data, meta }`. */
  recordsPage: (f: RecordsFilter & { page: number; per_page: number }) =>
    apiList<ProcessedAttendanceRecord>('/attendance/records', {
      query: { employee_id: f.employee_id, branch_id: f.branch_id, from: f.from, to: f.to, page: f.page, per_page: f.per_page },
    }),

  /** One page of raw punches with the total. */
  rawEventsPage: (f: RawEventsFilter & { page: number; per_page: number }) =>
    apiList<RawAttendanceEvent>('/attendance/raw-events', {
      query: {
        device_user_id: f.device_user_id,
        from: f.from,
        to: f.to,
        unresolved: f.unresolved ? 'true' : undefined,
        page: f.page,
        per_page: f.per_page,
      },
    }),

  exceptions: (f: ExceptionsFilter) =>
    apiRequest<AttendanceException[]>('/attendance/exceptions', {
      query: { ...f, per_page: 200 },
    }),

  /** Paged variant: the Exceptions tab needs `meta.total`. */
  exceptionsPage: (f: ExceptionsFilter, perPage: number = EXCEPTIONS_PER_PAGE) =>
    apiList<AttendanceException>('/attendance/exceptions', {
      query: { ...f, per_page: perPage } as Record<string, string | number | undefined>,
    }),

  summary: (date?: string, branchId?: string | null, range?: { from: string; to: string }) =>
    apiRequest<AttendanceSummary>('/attendance/summary', {
      query: { date, branch_id: branchId ?? undefined, from: range?.from, to: range?.to },
    }),

  rawEvents: (f: RawEventsFilter) =>
    apiRequest<RawAttendanceEvent[]>('/attendance/raw-events', {
      query: {
        device_user_id: f.device_user_id,
        from: f.from,
        to: f.to,
        unresolved: f.unresolved ? 'true' : undefined,
        per_page: 200,
      },
    }),

  process: (body: ProcessAttendanceBody) =>
    apiRequest<ProcessAttendanceResult>('/attendance/process', { method: 'POST', body }),

  addAdjustment: (recordId: string, body: CreateAdjustmentBody) =>
    apiRequest<CreateAdjustmentResult>(`/attendance/records/${recordId}/adjustments`, { method: 'POST', body }),

  reviewException: (id: string) =>
    apiRequest<AttendanceException>(`/attendance/exceptions/${id}/review`, { method: 'PATCH' }),

  resolveException: (id: string, body: ResolveExceptionBody) =>
    apiRequest<AttendanceException>(`/attendance/exceptions/${id}/resolve`, {
      method: 'PATCH',
      body,
    }),

  /** One-call resolve (Re-design T06): adjustment + resolution, or a dismissal. */
  quickResolveException: (id: string, body: QuickResolveBody) =>
    apiRequest<QuickResolveResult>(`/attendance/exceptions/${id}/quick-resolve`, { method: 'POST', body }),

  /** The API takes 1–100 ids per call; larger selections go in sequential chunks and the results are merged. */
  bulkResolveExceptions: async (body: BulkResolveBody): Promise<BulkResolveResult> => {
    const merged: BulkResolveResult = { resolved: [], failed: [] };
    const impacts: PayrollImpact[][] = [];
    for (let i = 0; i < body.exception_ids.length; i += BULK_RESOLVE_MAX) {
      const res = await apiRequest<BulkResolveResult>('/attendance/exceptions/bulk-resolve', {
        method: 'POST',
        body: { ...body, exception_ids: body.exception_ids.slice(i, i + BULK_RESOLVE_MAX) },
      });
      merged.resolved.push(...res.resolved);
      merged.failed.push(...res.failed);
      impacts.push(res.payroll_impact ?? []);
    }
    // One entry per payroll run across all chunks.
    const byRun = new Map<string, PayrollImpact>();
    for (const list of impacts) for (const i of list) byRun.set(i.run_id, i);
    merged.payroll_impact = [...byRun.values()];
    return merged;
  },

  activeShift: () => apiRequest<ActiveShiftStatus>('/attendance/active-shift'),
};
